import React from 'react';

/**
 * 将大纲原文（含 [封面]/[内容]/[总结] 标记和 <page> 标签）
 * 格式化为可展示的 React 元素。
 */
export const FormatOutlineView = ({ text }) => {
    if (!text) return <span className="text-gray-300">暂无大纲内容</span>;

    const cleaned = text.replace(/<\/?page>/gi, '').trim();
    const lines = cleaned.split('\n');
    const elements = [];
    let key = 0;

    for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed) { elements.push(<div key={key++} className="h-3" />); continue; }

        // Section markers: [封面] [内容] [总结]
        const sectionMatch = trimmed.match(/^\[(.+?)\]\s*(.*)/);
        if (sectionMatch) {
            const marker = sectionMatch[1];
            const rest = sectionMatch[2];
            let color = 'text-blue-600 bg-blue-50 border-blue-200';
            let label = marker;
            if (marker.includes('封面')) { color = 'text-purple-700 bg-purple-50 border-purple-200'; label = '封面'; }
            else if (marker.includes('总结')) { color = 'text-emerald-700 bg-emerald-50 border-emerald-200'; label = '总结'; }
            else if (marker.includes('内容')) { color = 'text-blue-700 bg-blue-50 border-blue-200'; label = '内容'; }
            elements.push(
                <div key={key++} className="mt-4 mb-2 first:mt-0">
                    <span className={`inline-block rounded-md border px-2 py-0.5 text-[11px] font-bold ${color}`}>{label}</span>
                    {rest && <span className="ml-2 text-sm font-semibold text-gray-800">{rest}</span>}
                </div>
            );
            continue;
        }
        // 配图提示行
        if (/^配图(建议|描述|：)/.test(trimmed)) {
            elements.push(<p key={key++} className="text-[12px] text-gray-400 italic mt-1">{trimmed}</p>);
            continue;
        }
        // 子标题行
        if (/^(标题|副标题|背景|关键点|正文|主标题)[：:]/.test(trimmed)) {
            const [label, ...rest] = trimmed.split(/[：:]/);
            elements.push(
                <p key={key++} className="text-[13px] leading-6 text-gray-700 mt-1">
                    <span className="font-semibold text-gray-800">{label}：</span>{rest.join('：')}
                </p>
            );
            continue;
        }
        // 列表行
        if (/^[•·\-–—]/.test(trimmed) || /^[0-9]+[.、]/.test(trimmed)) {
            elements.push(<p key={key++} className="text-[13px] leading-6 text-gray-600 pl-2">{trimmed}</p>);
            continue;
        }
        // 普通行
        elements.push(<p key={key++} className="text-[13px] leading-6 text-gray-600">{trimmed}</p>);
    }
    return <>{elements}</>;
};

/**
 * 将文本格式化为小红书正式文案风格的 React 元素。
 * 自动清理大纲标记（[封面]/[内容]/[总结]、配图建议等），
 * 输出干净的、可直接复制发布的内容。
 */
export const FormatCopywritingView = ({ text, fontSize = '16px' }) => {
    if (!text) return <span className="text-gray-300">暂无文案内容</span>;

    // 预处理：清理 <page> 标签和大纲标记
    const cleaned = text
        .replace(/<\/?page>/gi, '')
        // 移除独占一行的 [封面] [内容] [总结] 标记
        .replace(/^\[(?:封面|内容|总结)\]\s*$/gm, '')
        // 行首的 [封面] [内容] [总结] 后跟内容时只保留内容
        .replace(/^\[(?:封面|内容|总结)\]\s*/gm, '')
        // 移除配图建议/描述行
        .replace(/^配图(?:建议|描述|：).*$/gm, '')
        .trim();

    // 提取结构化标签后的纯内容: "标题：xxx" → "xxx"
    const extractValue = (line) => {
        const m = line.match(/^(?:标题|副标题|主标题|背景|关键点|正文)[：:]\s*(.*)/);
        return m ? m[1] : line;
    };

    const paragraphs = cleaned.split('\n');
    const elements = [];
    let key = 0;

    for (const line of paragraphs) {
        const trimmed = line.trim();
        if (!trimmed) { elements.push(<div key={key++} className="h-2" />); continue; }

        const content = extractValue(trimmed);
        if (!content) continue;

        // Bold headings: **text** or ## text
        if (/^\*\*.+\*\*$/.test(content) || /^#{1,3}\s+/.test(content)) {
            const clean = content.replace(/^#+\s*/, '').replace(/\*\*/g, '');
            elements.push(
                <p key={key++} className="font-bold text-[#333] mt-3 mb-1" style={{ fontSize }}>{clean}</p>
            );
            continue;
        }
        // Emoji-prefixed lines (common in XHS)
        if (/^[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}]/u.test(content)) {
            elements.push(
                <p key={key++} className="text-[#333] leading-relaxed" style={{ fontSize }}>{content}</p>
            );
            continue;
        }
        // List items
        if (/^[•·\-–—✅❌👉📌]/.test(content) || /^[0-9]+[.、)）]/.test(content)) {
            elements.push(
                <p key={key++} className="text-[#333] leading-relaxed pl-1" style={{ fontSize }}>{content}</p>
            );
            continue;
        }
        // Regular paragraph
        elements.push(
            <p key={key++} className="text-[#333] leading-relaxed" style={{ fontSize }}>{content}</p>
        );
    }
    return <>{elements}</>;
};

/**
 * 清理文案中的大纲标记和部分 Markdown，
 * 返回纯文本供复制使用。
 */
export const stripMarkdownForXHS = (text) => {
    if (!text) return '';
    return text
        .replace(/<\/?page>/gi, '')
        .replace(/^\[(?:封面|内容|总结)\]\s*$/gm, '')
        .replace(/^\[(?:封面|内容|总结)\]\s*/gm, '')
        .replace(/^配图(?:建议|描述|：).*$/gm, '')
        .replace(/\*\*(.*?)\*\*/g, '$1')
        .replace(/^#{1,6}\s+(.*)$/gm, '$1')
        .trim();
};
